import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ToastController } from '@ionic/angular';
import {
  Action,
  Actions,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  Store
} from '@ngxs/store';
import {
  catchError,
  delay,
  EMPTY,
  from,
  of,
  retry,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';

import {
  CardTerminalTypes,
  CreditCardProcessingType,
  FreedomPayResponse,
  NormalizedTicket,
  PaymentRequestType,
  Ticket,
  TicketStatus
} from 'src/app/models';
import { PaymentService, TicketService } from 'src/app/services';
import { FreedomPayService } from 'src/app/services/freedompay/freedompay.service';
import { GlobalService } from 'src/app/services/global/global.service';
import { IosLocalNetworkAuthService as IosLocalNetworkAuthService } from 'src/app/services/local-network-auth/local-network-auth.service';
import { ExceptionError, HttpRequestError } from '../error/error.action';
import {
  LoadTicketDetailSuccess,
  MealPlanPaymentError,
  RemoveTicketSuccess,
  UpdateTicketSuccess
} from '../tickets/tickets.action';
import {
  CancelGatewayTransaction,
  CancelGatewayTransactionSuccess,
  FinalizeMealPlanTicket,
  FinalizeMealPlanTicketError,
  FinalizeMealPlanTicketSuccess,
  GetBusinessLines,
  GetDepartments,
  GetGiftCard,
  GetGiftCardSuccess,
  MakePayment,
  MakePaymentError,
  MakePaymentFailed,
  MakePaymentGatewayTransactionSuccess,
  MakePaymentSuccess,
  MakePaymentTerminalTransactionSuccess,
  OnGatewayTransactionError,
  OnGatewayTransactionSuccess,
  OnRetryGatewayTransaction,
  PayOnPickup,
  PayOnPickupError,
  PayOnPickupSuccess,
  ResetDepartments,
  VoidTerminalTransaction,
  VoidTerminalTransactionError,
  VoidTerminalTransactionSuccess
} from './payment.action';
import { LOG_ROCKET_CUSTOM_EVENTS, LogRocketProvider } from 'src/app/providers';
import { TICKETS_STATE_TOKEN } from 'src/app/store/tickets/tickets.state';
import { PAYMENT_STATE_TOKEN, PaymentStateModel } from './payment.state.model';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { StartIdleTime, StopIdle } from 'src/app/store/app/app.action';
import { RefundClose } from '../transaction-lookup/transaction-lookup.action';
import { map } from 'rxjs/operators';

@State({
  name: PAYMENT_STATE_TOKEN,
  defaults: {
    departments: [],
    businessLines: [],
    selectedGiftCard: null,
    lastGatewayTransaction: null
  }
})
@Injectable()
export class PaymentState {
  errorMsg: string;

  constructor(
    private store: Store,
    private gbService: GlobalService,
    private ticketService: TicketService,
    private paymentService: PaymentService,
    private freedomPayService: FreedomPayService,
    private logRocketProvider: LogRocketProvider,
    private toastController: ToastController,
    private ngZone: NgZone,
    private action$: Actions,
    private iosLocalNetworkAuthService: IosLocalNetworkAuthService
  ) {}

  @Selector()
  static businessLines(state: PaymentStateModel) {
    return state.businessLines;
  }

  @Selector()
  static departments(state: PaymentStateModel) {
    return state.departments;
  }

  @Selector()
  static lastGatewayTransaction(state: PaymentStateModel) {
    return state.lastGatewayTransaction;
  }

  //#region Actions

  //Call the request to get the Departments
  @Action(GetBusinessLines)
  getBusinessLines({ patchState }: StateContext<PaymentStateModel>) {
    return this.gbService
      .getBusinessLines()
      .pipe(tap((businessLines) => patchState({ businessLines })));
  }

  //Call the request to get the Departments
  @Action(GetDepartments)
  getDepartments(
    { patchState }: StateContext<PaymentStateModel>,
    { search_text, business_line_id }: GetDepartments
  ) {
    return this.gbService
      .getDepartments(search_text, business_line_id)
      .pipe(tap((departments) => patchState({ departments })));
  }

  @Action(ResetDepartments)
  resetDepartments({ patchState }: StateContext<PaymentStateModel>) {
    patchState({ departments: [] });
  }

  //Call the request to make the payment
  @Action(MakePayment)
  makePayment(
    ctx: StateContext<PaymentStateModel>,
    { ticket, payment_request, closed_from_source }: MakePayment
  ) {
    if (ticket) {
      const location = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
      const isCreditCardProcessing =
        payment_request.type === PaymentRequestType.CREDIT_CARD &&
        location.credit_card_processing !== CreditCardProcessingType.None;
      if (isCreditCardProcessing) {
        if (
          payment_request.terminal_type &&
          payment_request.terminal_type !== CardTerminalTypes.FREEDOMPAY
        ) {
          ctx.dispatch(new StopIdle());
          this.action$
            .pipe(
              ofActionSuccessful(
                MakePaymentFailed,
                OnGatewayTransactionSuccess,
                OnGatewayTransactionError
              ),
              take(1)
            )
            .subscribe(() => ctx.dispatch(new StartIdleTime()));
        }

        // Route to appropriate payment processing method
        if (
          payment_request.terminal_type &&
          payment_request.terminal_type === CardTerminalTypes.FREEDOMPAY
        ) {
          return this.processTerminalPayment(ctx, ticket, payment_request);
        } else {
          return this.processGatewayPayment(
            ctx,
            ticket,
            payment_request,
            closed_from_source
          );
        }
      }

      // Handle non-credit card payments (cash, gift card, etc.)
      return this.processDirectPayment(
        ctx,
        ticket,
        payment_request,
        closed_from_source
      );
    }
  }

  private processGatewayPayment(
    ctx: StateContext<PaymentStateModel>,
    ticket: any,
    payment_request: any,
    closed_from_source?: any
  ) {
    // Handle gateway processors (Trancloud, Stripe, etc.)
    return this.ticketService
      .makePaymentGatewayTransaction({
        ticket_uuid: ticket.device_ticket_uuid,
        paymentRequest: payment_request,
        closed_from_source
      })
      .pipe(
        tap((result) =>
          ctx.dispatch(
            new MakePaymentGatewayTransactionSuccess(
              result,
              ticket,
              payment_request
            )
          )
        ),
        catchError((error: HttpErrorResponse) =>
          ctx.dispatch(
            new MakePaymentFailed(error, { ticket, payment_request })
          )
        )
      );
  }

  /**
   * Dispatches an error action with a delay to ensure UI components
   * (particularly the GatewayTransactionModal) have time to subscribe.
   * @param ctx State context
   * @param errorMessage Error message to display
   * @returns Observable that completes after dispatching the error
   */
  private dispatchDelayedGatewayError(
    ctx: StateContext<PaymentStateModel>,
    errorMessage: string
  ) {
    const safeBufferDelayMilliSeconds = 300;
    return of(null).pipe(
      delay(safeBufferDelayMilliSeconds),
      tap(() => {
        ctx.dispatch(new OnGatewayTransactionError(errorMessage));
      }),
      switchMap(() => EMPTY)
    );
  }

  /**
   * Checks for local network permissions and terminal connectivity and proceeds to create a backend transaction and make a payment
   */
  private processTerminalPayment(
    ctx: StateContext<PaymentStateModel>,
    ticket: any,
    payment_request: any
  ) {
    // Check local network permission first (iOS only)
    return from(this.iosLocalNetworkAuthService.requestPermission()).pipe(
      switchMap((permissionResult) => {
        // If on iOS and permission was denied, show error
        if (permissionResult && !permissionResult.granted) {
          ctx.patchState({
            lastGatewayTransaction: {
              ticket,
              paymentRequest: payment_request
            }
          });
          return this.dispatchDelayedGatewayError(
            ctx,
            'Cannot connect to payment terminal. Enable Local Network permission in Settings > Privacy & Security > Local Network > MealSuite POS to proceed.'
          );
        }

        // Check for terminal connectivity before proceeding to make payment
        return this.freedomPayService
          .testTerminalConnection(payment_request.payment_terminal_id)
          .pipe(
            switchMap((isConnected) => {
              if (!isConnected) {
                ctx.patchState({
                  lastGatewayTransaction: {
                    ticket,
                    paymentRequest: payment_request
                  }
                });
                return this.dispatchDelayedGatewayError(
                  ctx,
                  'Unable to connect to payment terminal. Please check terminal connection and try again.'
                );
              }

              // Terminal is connected, proceed with payment
              // Override payment type for FreedomPay terminals
              if (
                payment_request.terminal_type === CardTerminalTypes.FREEDOMPAY
              ) {
                payment_request.type =
                  PaymentRequestType.CREDIT_CARD_FREEDOMPAY;
              }

              // First create a gateway transaction record
              return this.ticketService
                .makePaymentGatewayTransaction({
                  ticket_uuid: ticket.device_ticket_uuid,
                  paymentRequest: payment_request
                })
                .pipe(
                  tap((gatewayResult) => {
                    if (!gatewayResult?.gateway_transaction_id) {
                      throw new Error(
                        'Failed to create gateway transaction record'
                      );
                    }

                    // Store the transaction info for retry functionality
                    ctx.patchState({
                      lastGatewayTransaction: {
                        ticket,
                        paymentRequest: payment_request,
                        result: gatewayResult
                      }
                    });
                  }),
                  switchMap((gatewayResult) =>
                    this.freedomPayService
                      .processPayment(ticket, payment_request)
                      .pipe(
                        map((freedomPayResponse) => ({
                          gatewayResult,
                          freedomPayResponse
                        }))
                      )
                  ),
                  tap(({ gatewayResult, freedomPayResponse }) => {
                    console.log('FreedomPay response:', freedomPayResponse);
                    const isAcceptedOffline =
                      this.freedomPayService.isOfflineTransactionAccepted(
                        freedomPayResponse
                      );
                    //TODO: work on offline decline
                    // const isDeclined =
                    //   this.freedomPayService.isOfflineTransactionRejected(
                    //     freedomPayResponse
                    //   );

                    // Store offline transaction for replay
                    if (isAcceptedOffline) {
                      console.log('Storing offline transaction for replay');
                      this.freedomPayService
                        .storeTransactionRequest(
                          freedomPayResponse,
                          gatewayResult.gateway_transaction_id,
                          ticket.id,
                          ticket.device_ticket_uuid,
                          payment_request.payment_terminal_id,
                          payment_request.amount.toString()
                        )
                        .catch((error) => {
                          console.error(
                            'Failed to store offline transaction:',
                            error
                          );
                        });
                    }

                    const isSuccessful =
                      this.freedomPayService.isTransactionSuccessful(
                        freedomPayResponse
                      );

                    if (isSuccessful || isAcceptedOffline) {
                      ctx.dispatch(
                        new MakePaymentTerminalTransactionSuccess(
                          gatewayResult.gateway_transaction_id,
                          freedomPayResponse,
                          ticket,
                          payment_request
                        )
                      );
                    } else {
                      const timedOut =
                        this.freedomPayService.isTransactionTimedOut(
                          freedomPayResponse
                        );
                      const userCancelledTransaction =
                        this.freedomPayService.userCancelledTransaction(
                          freedomPayResponse
                        );
                      if (timedOut) {
                        ctx.dispatch(
                          new OnGatewayTransactionError(
                            'Payment request timed out. The transaction has been cancelled.!!!'
                          )
                        );
                      } else if (userCancelledTransaction) {
                        ctx.dispatch(
                          new OnGatewayTransactionError(
                            'Transaction cancelled by the customer'
                          )
                        );
                      } else {
                        const errorMessage =
                          this.freedomPayService.getErrorMessage(
                            freedomPayResponse
                          );
                        ctx.dispatch(
                          new OnGatewayTransactionError(errorMessage)
                        );
                      }
                    }
                  }),
                  catchError((terminalError: Error) => {
                    const errorMessage = `Terminal communication failed: ${terminalError.message}`;

                    // Dispatch error immediately for terminal communication failures
                    ctx.dispatch(new OnGatewayTransactionError(errorMessage));
                    return EMPTY;
                  })
                );
            })
          );
      })
    );
  }

  private processDirectPayment(
    ctx: StateContext<PaymentStateModel>,
    ticket: Ticket | NormalizedTicket,
    payment_request: any,
    closed_from_source?: any
  ) {
    // Handle non-credit card payments (cash, gift card, meal plan, etc.)
    return this.ticketService
      .makePaymentTransactions({
        payment_request,
        closed_from_source,
        ticket_uuid: ticket.device_ticket_uuid
      })
      .pipe(
        tap((response) => {
          this.logRocketProvider.track(
            LOG_ROCKET_CUSTOM_EVENTS.Transaction.Complete,
            {
              device_ticket_uuid: ticket.device_ticket_uuid,
              type: payment_request.type
            }
          );

          const actions: any[] = [
            new MakePaymentSuccess(
              ticket.device_ticket_uuid,
              response,
              payment_request
            )
          ];

          if (response.ticket.status !== TicketStatus.CLOSED) {
            actions.push(new LoadTicketDetailSuccess(response.ticket));
          }

          ctx.dispatch(actions);
        }),
        catchError((error: HttpErrorResponse) =>
          ctx.dispatch(
            new MakePaymentFailed(error, { ticket, payment_request })
          )
        )
      );
  }

  private handleFreedomPaySuccess(
    ticket: Ticket | NormalizedTicket,
    transaction_id: number,
    freedomPayResponse: FreedomPayResponse
  ) {
    // return throwError(() => new Error('Simulated timeout error'));
    return this.ticketService
      .updateTerminalTransactionResult(
        ticket.device_ticket_uuid,
        transaction_id,
        freedomPayResponse
      )
      .pipe(catchError(() => throwError(() => new Error('UpdateFailed'))));
  }

  @Action(MakePaymentGatewayTransactionSuccess)
  makePaymentGatewayTransactionSuccess(
    { patchState, getState, dispatch }: StateContext<PaymentStateModel>,
    { result, ticket, paymentRequest }: MakePaymentGatewayTransactionSuccess
  ) {
    this.logRocketProvider.track(
      LOG_ROCKET_CUSTOM_EVENTS.Transaction.InProgress,
      {
        device_ticket_uuid: ticket.device_ticket_uuid,
        type: paymentRequest.type
      }
    );
    patchState({
      lastGatewayTransaction: {
        ticket,
        paymentRequest,
        result,
        stripe_payment_secret: '',
        stripe_pk_production: '',
        stripe_pk_test: '',
        stripe_account_id: ''
      }
    });

    const selectedTicketId =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket.data;

    return this.ticketService
      .getTrancloudTransaction(selectedTicketId, result.gateway_transaction_id)
      .pipe(
        tap((response) => {
          switch (response.status) {
            case 'in_progress':
              const lastGatewayTransaction = getState().lastGatewayTransaction;
              const isStripePaymentSecretSet =
                !!lastGatewayTransaction?.stripe_payment_secret;

              //We only need to set this once during this transaction
              if (response.stripe_payment_secret && !isStripePaymentSecretSet) {
                patchState({
                  lastGatewayTransaction: {
                    ...lastGatewayTransaction,
                    stripe_payment_secret: response.stripe_payment_secret,
                    stripe_pk_test: response.stripe_pk_test,
                    stripe_pk_production: response.stripe_pk_production,
                    stripe_account_id: response.stripe_account_id
                  }
                });
              }
              throw response;
            case 'complete':
              this.logRocketProvider.track(
                LOG_ROCKET_CUSTOM_EVENTS.Transaction.Complete,
                {
                  device_ticket_uuid: selectedTicketId,
                  transaction_id: response.transaction?.id,
                  type: response.transaction?.type
                }
              );
              dispatch([
                new OnGatewayTransactionSuccess(),
                new MakePaymentSuccess(
                  selectedTicketId,
                  {
                    transaction: response.transaction,
                    ticket_status: response.ticket_status,
                    new_balance: response.new_balance
                  },
                  paymentRequest
                )
              ]);
              break;
            default:
              this.logRocketProvider.track(
                LOG_ROCKET_CUSTOM_EVENTS.Transaction.Declined,
                {
                  device_ticket_uuid: selectedTicketId
                }
              );

              //Default Gateway Transaction error
              let error = 'There was a problem with the gateway transaction';

              // Gateway Transaction payment_gateway_response value will be different depending on the payment processor:
              // Stripe - String value returned with readable error
              // Trancloud - Object returned, readable error within Rstream.TextResponse
              const payment_gateway_response =
                response?.payment_gateway_response;
              const RStream =
                payment_gateway_response?.RStream ||
                payment_gateway_response?.Rstream;
              const CmdStatus = RStream?.CmdStatus;
              const TextResponse = RStream?.TextResponse;

              if (typeof payment_gateway_response === 'string') {
                error = payment_gateway_response;
              } else if (TextResponse) {
                error = TextResponse;
              }

              dispatch(new OnGatewayTransactionError(error, CmdStatus));
          }
        }),
        catchError((e: HttpErrorResponse) => {
          //Action should only be dispatched if there is an actual backend error response.
          //This catchError is triggered when response status is in_progress due to the throw on the above Switch Case to trigger the retry operator.
          if (e.error?.message === 'failed') {
            dispatch(
              new OnGatewayTransactionError('Gateway transaction cancelled')
            );
            return EMPTY;
          }

          return throwError(() => e);
        }),
        retry({ delay: 2000 })
      );
  }

  @Action(MakePaymentTerminalTransactionSuccess)
  makePaymentTerminalTransactionSuccess(
    { patchState, dispatch }: StateContext<PaymentStateModel>,
    {
      transactionId,
      freedomPayResponse,
      ticket,
      paymentRequest
    }: MakePaymentTerminalTransactionSuccess
  ) {
    this.logRocketProvider.track(
      LOG_ROCKET_CUSTOM_EVENTS.Transaction.InProgress,
      {
        device_ticket_uuid: ticket.device_ticket_uuid,
        type: paymentRequest.type
      }
    );

    patchState({
      lastTerminalTransaction: {
        ticket,
        paymentRequest,
        freedomPayResponse
      }
    });

    const selectedTicketId =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket.data;

    return this.handleFreedomPaySuccess(
      ticket,
      transactionId,
      freedomPayResponse
    ).pipe(
      tap((response) => {
        this.logRocketProvider.track(
          LOG_ROCKET_CUSTOM_EVENTS.Transaction.Complete,
          {
            device_ticket_uuid: selectedTicketId,
            transaction_id: response.transaction?.id,
            type: response.transaction?.type
          }
        );

        dispatch([
          new OnGatewayTransactionSuccess(),
          new MakePaymentSuccess(selectedTicketId, response, paymentRequest)
        ]);
      }),
      catchError((error) => {
        dispatch([
          new VoidTerminalTransaction(
            ticket,
            freedomPayResponse,
            paymentRequest.payment_terminal_id,
            false
          ),
          new MakePaymentFailed(error, {
            ticket,
            payment_request: paymentRequest
          })
        ]);
        const errorMessage =
          'Terminal payment succeeded but failed to record transaction';
        dispatch(new OnGatewayTransactionError(errorMessage));
        return EMPTY;
      })
    );
  }

  @Action(MakePaymentFailed)
  makePaymentFailed(
    ctx: StateContext<PaymentStateModel>,
    { error, paymentData }: MakePaymentFailed
  ) {
    const isCreditCard =
      paymentData.payment_request.type === PaymentRequestType.CREDIT_CARD ||
      paymentData.payment_request.terminal_type ===
        CardTerminalTypes.FREEDOMPAY;
    if (isCreditCard) {
      ctx.patchState({
        lastGatewayTransaction: {
          ticket: paymentData.ticket,
          paymentRequest: paymentData.payment_request
        }
      });
    }

    const errorMessage: string = error.error?.message;
    return ctx.dispatch(
      errorMessage?.includes('Insufficient mealplan')
        ? new MealPlanPaymentError(
            paymentData.ticket.device_ticket_uuid,
            error.error?.message
          )
        : new MakePaymentError(error, paymentData)
    );
  }

  @Action(MakePaymentError)
  makePaymentError(
    ctx: StateContext<PaymentStateModel>,
    { error: httpError, posPaymentData }: MakePaymentError
  ) {
    const paymentRequest = posPaymentData.payment_request;
    const { status, error } = httpError;
    const httpErrorMsg = error?.message;
    let errorMessage =
      httpErrorMsg ||
      'There was an error processing your payment. Please try again or contact support.';

    if (paymentRequest.type === PaymentRequestType.CHARGE_ACCOUNT) {
      if (status === 422) {
        errorMessage = `Your payment was not processed due to insufficient funds remaining for the selected account. <br><br>${httpErrorMsg}<br><br>
        Choose another payment option, and try again.`;
      }
    }

    ctx.dispatch(new HttpRequestError(errorMessage));
  }

  @Action(OnRetryGatewayTransaction)
  retryGatewayTransaction(ctx: StateContext<PaymentStateModel>) {
    const { lastGatewayTransaction } = ctx.getState();
    return ctx.dispatch(
      lastGatewayTransaction
        ? new MakePayment(
            lastGatewayTransaction.ticket,
            lastGatewayTransaction.paymentRequest
          )
        : new ExceptionError(
            'There was an error processing your payment. Please try again or contact support.'
          )
    );
  }

  @Action(OnGatewayTransactionSuccess)
  onGatewayTransactionSuccess(ctx: StateContext<PaymentStateModel>) {
    ctx.patchState({ lastGatewayTransaction: null });
  }

  @Action(GetGiftCard)
  getGiftCard(
    ctx: StateContext<PaymentStateModel>,
    { identification_number }: GetGiftCard
  ) {
    return this.gbService.getGiftCard(identification_number).pipe(
      tap((gift_card) => {
        ctx.dispatch(new GetGiftCardSuccess(gift_card));
      })
    );
  }

  @Action(CancelGatewayTransaction)
  onCancelGatewayTransaction({
    getState,
    dispatch
  }: StateContext<PaymentStateModel>) {
    const { ticket, result } = getState().lastGatewayTransaction;

    return this.paymentService
      .cancelGatewayTransaction(
        ticket.device_ticket_uuid,
        result.gateway_transaction_id
      )
      .pipe(
        tap(({ success, payment_received, error_message }) => {
          dispatch(
            success
              ? new CancelGatewayTransactionSuccess()
              : new ExceptionError(
                  payment_received
                    ? error_message
                    : 'There was a problem while cancelling the gateway transaction'
                )
          );
        }),
        catchError(() =>
          dispatch(
            new ExceptionError(
              'There was a problem while cancelling the gateway transaction'
            )
          )
        )
      );
  }

  @Action(PayOnPickup)
  payOnPickup(
    ctx: StateContext<PaymentStateModel>,
    { deviceTicketUuid }: PayOnPickup
  ) {
    return this.paymentService.payOnPickup(deviceTicketUuid).pipe(
      tap((res) => ctx.dispatch(new PayOnPickupSuccess(res))),
      catchError(() =>
        ctx.dispatch(
          new PayOnPickupError(`There was a problem placing the order`)
        )
      )
    );
  }

  @Action(PayOnPickupSuccess)
  payOnPickupSuccess(
    ctx: StateContext<PaymentStateModel>,
    { ticket }: PayOnPickupSuccess
  ) {
    const isAutoCloseTicket =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location
        .auto_close_on_full_payment;

    this.toastPlaceOrderSuccess();

    if (isAutoCloseTicket) {
      return ctx.dispatch(new RemoveTicketSuccess(ticket.device_ticket_uuid));
    }

    return ctx.dispatch(new UpdateTicketSuccess(ticket));
  }

  @Action(FinalizeMealPlanTicket)
  finalizeMealPlanTicket(
    { dispatch }: StateContext<PaymentStateModel>,
    { deviceTicketUuid }: FinalizeMealPlanTicket
  ) {
    return this.paymentService.finalizeMealPlanTicket(deviceTicketUuid).pipe(
      switchMap((res) =>
        res.success
          ? dispatch(new FinalizeMealPlanTicketSuccess(res.ticket))
          : dispatch(
              new FinalizeMealPlanTicketError(
                'There was a problem placing the order'
              )
            )
      ),
      catchError((error: HttpErrorResponse) =>
        dispatch(
          new FinalizeMealPlanTicketError(
            'There was a problem placing the order',
            error
          )
        )
      )
    );
  }

  @Action(FinalizeMealPlanTicketSuccess)
  finalizeMealPlanTicketSuccess(
    { dispatch }: StateContext<PaymentStateModel>,
    { ticket }: FinalizeMealPlanTicketSuccess
  ) {
    return dispatch(
      new RemoveTicketSuccess(ticket.device_ticket_uuid)
    ).subscribe(() => this.toastPlaceOrderSuccess());
  }

  @Action(VoidTerminalTransaction)
  voidTerminalTransaction(
    ctx: StateContext<PaymentStateModel>,
    {
      ticket,
      freedomPayResponse,
      terminalId,
      isRefund
    }: VoidTerminalTransaction
  ) {
    const operationType = isRefund ? 'refund' : 'payment';

    const voidMethod$ = isRefund
      ? this.freedomPayService.voidRefund(
          ticket,
          freedomPayResponse,
          terminalId
        )
      : this.freedomPayService.voidPayment(
          ticket,
          freedomPayResponse,
          terminalId
        );

    return voidMethod$.pipe(
      tap((voidResponse) => {
        ctx.dispatch(
          new VoidTerminalTransactionSuccess(voidResponse, isRefund)
        );
      }),
      catchError(() => {
        ctx.dispatch(
          new VoidTerminalTransactionError(
            `Critical error: ${operationType} succeeded but system update failed and void failed. Contact support immediately.`,
            isRefund
          )
        );
        return EMPTY;
      })
    );
  }

  @Action(VoidTerminalTransactionSuccess)
  voidTerminalTransactionSuccess(
    ctx: StateContext<PaymentStateModel>,
    { voidResponse, isRefund }: VoidTerminalTransactionSuccess
  ) {
    if (this.freedomPayService.isTransactionSuccessful(voidResponse)) {
      const successMessage = isRefund
        ? 'Unable to communicate with POS Server. The refund has been voided.'
        : 'Unable to communicate with POS Server. The transaction has been voided.';

      ctx.dispatch(
        isRefund
          ? new RefundClose()
          : new OnGatewayTransactionError(successMessage)
      );
    } else {
      const errorMessage = isRefund
        ? 'Critical error: Refund succeeded but system update failed. Contact support immediately.'
        : 'Critical error: Payment succeeded but system update failed. Contact support immediately.';

      ctx.dispatch(
        isRefund
          ? new RefundClose()
          : new OnGatewayTransactionError(errorMessage)
      );
    }
  }

  @Action(VoidTerminalTransactionError)
  voidTerminalTransactionError(
    ctx: StateContext<PaymentStateModel>,
    { errorMessage }: VoidTerminalTransactionError
  ) {
    ctx.dispatch(new OnGatewayTransactionError(errorMessage));
  }

  //#endregion

  private toastPlaceOrderSuccess() {
    this.ngZone.run(async () => {
      const toast = await this.toastController.create({
        color: 'success',
        message: 'Order has been successfully placed',
        duration: 4000
      });
      await toast.present();
    });
  }
}
